/**
 * 椭圆族选择状态 Store。
 * 统一管理椭圆、部分椭圆和抛物线的当前选中对象与变化事件。
 */

import { defineStore } from 'pinia';
import type {
  SelectionUpdatedEventPayload,
  SketchEllipseObject,
  SketchParabolaObject,
} from '@puppycad/cad-core';
import {
  EntityInteractionEventType,
  isSketchEllipseObject,
  isSketchParabolaObject,
} from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObject,
} from './viewer-selection-helpers';

export type SelectedEllipseFamilyObject = SketchEllipseObject | SketchParabolaObject;

export const useGetSelectEllipse = defineStore('get-select-ellipse', () => {
  let selectedEllipse: SelectedEllipseFamilyObject | null = null;
  let subscriptions: Subscription[] = [];
  let hasSubscribed = false;
  const ellipseChanged$ = new BehaviorSubject<SelectedEllipseFamilyObject | null>(null);
  const { getViewer } = useViewer();

  function isEllipseFamilyObject(object: unknown): object is SelectedEllipseFamilyObject {
    return isSketchEllipseObject(object) || isSketchParabolaObject(object);
  }

  function findEllipseFamilyObject(objects: readonly unknown[]): SelectedEllipseFamilyObject | null {
    return objects.find(isEllipseFamilyObject) ?? null;
  }

  function emitSelectedEllipse(nextSelectedEllipse: SelectedEllipseFamilyObject | null): void {
    selectedEllipse = nextSelectedEllipse;
    ellipseChanged$.next(selectedEllipse);
  }

  function resolveEllipseFromSelectionSnapshot(
    payload: SelectionUpdatedEventPayload,
  ): SelectedEllipseFamilyObject | null {
    /**
     * SelectionUpdated 是当前选择的稳定快照。抛物线点击可能先命中控制点/代理对象，
     * 因此必须同时检查选择语义对象与原始命中对象，不能只信 payload.object。
     */
    return findEllipseFamilyObject([
      payload.object,
      ...payload.selectedObjects,
      payload.selectionSourceObject,
      ...payload.selectionSourceObjects,
    ]);
  }

  function syncFromManager(): void {
    emitSelectedEllipse(resolveViewerSelectedObject(
      getViewer(),
      isEllipseFamilyObject,
    ));
  }

  function handleSelectionUpdated(payload: SelectionUpdatedEventPayload): void {
    emitSelectedEllipse(resolveEllipseFromSelectionSnapshot(payload));
  }

  function handleSelectionCleared(): void {
    emitSelectedEllipse(null);
  }

  function subscribe(): void {
    const viewer = getViewer();
    const selectionUpdated$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionUpdated);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    if (!selectionUpdated$ || !selectionCleared$ || hasSubscribed) {
      return;
    }
    syncFromManager();
    subscriptions = [
      selectionUpdated$.subscribe((payload) => {
        handleSelectionUpdated(payload as SelectionUpdatedEventPayload);
      }),
      selectionCleared$.subscribe(handleSelectionCleared),
    ];
    hasSubscribed = true;
  }

  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSubscribed = false;
  }

  return {
    getSelected: (): SelectedEllipseFamilyObject | null => selectedEllipse,
    onChanged: (): Observable<SelectedEllipseFamilyObject | null> => ellipseChanged$.asObservable(),
    subscribe,
    unsubscribe,
  };
});
